import { useEffect, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { i18n } from '@lingui/core';
import { t } from '@lingui/core/macro';
import { I18nProvider } from '@lingui/react';
import { Banner, HelpIcon, useNotificationsStore } from '@thermon-platforms/thermon-ui-core';
import useDevicesStore from 'store/devices';
import useLicenseStore from 'store/license';
import useLocalStore from 'store/local';
import api from 'util/api';
import { getConfigItem } from 'util/device';


function ExpirationModal({expiration, close}) {
  const dismissable = expiration > 0;
  const [timer, setTimer] = useState(10);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState();
  const [error, setError] = useState();
  const [devicesStore, devicesStoreActions] = useDevicesStore();
  const [, licenseStoreActions] = useLicenseStore();
  const [, notificationsStoreActions] = useNotificationsStore();
  const [, localStoreActions] = useLocalStore();
  const [site, setSite] = useState();

  function decrementTimer() {
    setTimer(timer - 1);
  }

  useEffect(() => {
    if (!devicesStore.devices.length) return;
    devicesStoreActions.fetchDetail(devicesStore.devices.find(d => d.type == 'Site')?.id).then((detail) => {
      setSite(detail);
    });
  }, [devicesStore]);

  useEffect(() => {
    if (timer == 0 || !dismissable) return;
    setTimeout(decrementTimer, 1000);
  }, [timer]);

  function handleUpload() {
    const formData = new FormData();
    formData.append('licenseFile', file, file.name);
    setError(undefined);
    setLoading(true);

    api(`/auth/license`, {
      method: 'POST',
      body: formData,
      headers: {
        // This is an awkward hack to remove the Content-Type
        // header which let's the browser select the correct
        // type for upload. Unfortunately, just setting it to
        // multipart/form-data; doesn't work
      }
    })
      .then(async res => {
        const expiry = await res.json();
        setFile(undefined);
        if (expiry > 0) {
          licenseStoreActions.query();
          notificationsStoreActions.clearGlobalNotification();
          close();
        } else {
          setError(t`License file is invalid or expired`);
        }
      })
      .catch(() => {
        setError(t`License upload failed`);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function handleClose() {
    localStoreActions.setLastSawLicenseInterrupt(Date.now());
    close();
  }

  return (
    <I18nProvider i18n={i18n}>
      <header>
        <h3>
          {expiration > 0 ?
            <Trans>Your license expires in {expiration} days.</Trans>
            :
            <Trans>Your Thermon Egis license has expired.</Trans>
          }
        </h3>
      </header>
      <section>
        <Banner status="alert" small={true} show={error}>
          {error}
        </Banner>
        {expiration >= 2 ?
          <>
            <p>
              <Trans>
                <strong>Attention</strong>: Your Thermon Egis license will expire in {expiration} days.
                Once expired, the software will no longer function until the license is renewed.
              </Trans>
            </p>
            <p>
              <Trans>
                To avoid any disruption, please contact Thermon to renew your license.
              </Trans>
            </p>
          </>
        : <></>}
        {expiration == 1 ?
          <>
            <p>
              <Trans>
                <strong>Attention</strong>: Your Thermon Egis license will expire at the end of the day today.
                Once expired, the software will no longer function until the license is renewed.
              </Trans>
            </p>
            <p>
              <Trans>
                To avoid disruption, please contact Thermon immediately to renew your license.
              </Trans>
            </p>
          </>
        : <></>}
        {expiration <= 0 ?
          <>
            <p>
              <Trans>
                Please contact Thermon to renew your license.
              </Trans>
            </p>
          </>
        : <></>}
        {expiration <= 0 ? <ul className="form">
          <li>
            <label htmlFor="license-upload">
              <Trans>Add License</Trans>
            </label>
            <input
              type="file"
              id="license-upload"
              accept=".genesisnetworklicense"
              onChange={event => setFile(event.target.files[0])}
              disabled={loading}
            />
          </li>
          <li>
            <Trans>Your site id is:</Trans> <strong>{site ? getConfigItem(site, 'SiteId')?.value : ''}</strong>
            <HelpIcon text={t`The site ID is required when requesting a new license file.`} />
          </li>
          <li>
            <button
              className={`primary ${loading ? 'processing' : ''}`}
              disabled={loading || !file}
              onClick={handleUpload}
            >
              <Trans>Upload License</Trans>
            </button>
          </li>
        </ul> : <></>}
      </section>
      {dismissable ? <footer>
        <button onClick={handleClose} disabled={timer > 0}>
          <Trans>Dismiss</Trans>{timer ? <>&nbsp;<Trans>in</Trans> <span>{timer}</span></> : <></>}
        </button>
      </footer> : <></>}
    </I18nProvider>
  );
}

export default ExpirationModal;
